import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { TextInput } from "@/components/ui/TextInput";
import { EmailInput } from "@/components/ui/EmailInput";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { ClientsInput } from "@/components/inputs/ClientsInput";
import { PreviewSection } from "../shared";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { ContactType } from "@/types/db/contacts";
import { supabase } from "@/lib/supabase-client";

export interface ContactData {
  id: string;
  first_name: string;
  last_name: string;
  email_address?: string;
  phone_number?: string;
  meta_data?: {
    preferred_name?: string;
  };
  // Clients from link.clients_contacts joined with public.clients
  contact_clients?: {
    client: {
      id: string;
      name: string;
      status?: string;
      meta_data?: any;
    };
    note?: string;
  }[];
}

export interface ContactOverviewTabProps {
  contact: ContactData | null;
  loading?: boolean;
}

export function ContactOverviewTab({ contact, loading = false }: ContactOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<ContactType>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [emailChecking, setEmailChecking] = useState(false);

  // Check if email already exists (debounced) - only in edit mode
  useEffect(() => {
    if (!editing) {
      setEmailError(undefined);
      setEmailChecking(false);
      return;
    }

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const email = formData?.email_address?.trim().toLowerCase();
    const originalEmail = contact?.email_address?.trim().toLowerCase();

    // Clear error and skip check if email is empty, invalid format, or unchanged
    if (!email || !EMAIL_REGEX.test(email) || email === originalEmail) {
      setEmailError(undefined);
      setEmailChecking(false);
      return;
    }

    setEmailChecking(true);
    const timeoutId = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from("contacts")
          .select("id")
          .ilike("email_address", email)
          .neq("id", contact?.id || "")
          .limit(1);

        if (error) {
          console.error("Error checking email:", error);
          setEmailError(undefined);
        } else if (data && data.length > 0) {
          setEmailError("Email already exists");
        } else {
          setEmailError(undefined);
        }
      } catch (err) {
        console.error("Error checking email:", err);
        setEmailError(undefined);
      } finally {
        setEmailChecking(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData?.email_address, contact?.email_address, contact?.id, editing]);

  // Use formData when editing, otherwise use contact data
  const displayData = editing ? formData : contact;

  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={{ gap: 16, padding: 16 }}>
        <PreviewSection title="Contact Information">
          <EmailInput
            label="Email"
            value={editing ? formData?.email_address || "" : displayData?.email_address || ""}
            onChangeText={(text) => updateFormData({ ...formData, email_address: text })}
            error={emailError}
            checking={emailChecking}
            editable={editing}
            loading={loading}
          />
          <PhoneInput
            label="Phone"
            value={editing ? formData?.phone_number || "" : displayData?.phone_number || ""}
            onChangeText={(text) => updateFormData({ ...formData, phone_number: text })}
            editable={editing}
            loading={loading}
          />
          <TextInput
            label="Preferred Name"
            value={displayData?.meta_data?.preferred_name || ""}
            onChangeText={(text) =>
              updateFormData({
                ...formData,
                meta_data: { ...formData.meta_data, preferred_name: text },
              })
            }
            editable={editing}
            loading={loading}
          />
        </PreviewSection>

        <ClientsInput
          value={displayData?.contact_clients || []}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(clients) => updateFormData({ ...formData, contact_clients: clients })}
        />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
